// converter for data for each product on listing page
import { createListingPageUrlFromAttributes } from '../listingPageUrlParser/listingPageUrlParser';
import {
  convertImageUrlFromBrainToSmallerImage,
  getPageUrlPrefixForSegmentListingPage, getSingleSelectedBrandFilter, getSingleSelectedTypeFilter,
  normalizePriceInfoForProduct
} from './helpers/converterHelper';
import Configuration from '../../../configuration';
import { convertFiltersFromSearchLabsToReduxFormat } from './searchLabsFiltersConverter';
import UISettings from '../settings/UISettings';

const log = require('loglevel');


export function convertProductsDataToReduxFormat(
  {
    searchLabsRequestAttrs = {},
    searchLabsAPIData,
    productsDataFromStoreAPI = [],
    productIds = [],
    ucmsHierarchy = {}
  }) {

  const pageData = {};

  // if query is not empty string, it is search listing page
  pageData.searchListingPage = !!searchLabsRequestAttrs.query || !!searchLabsRequestAttrs?.filterAttributes?.tag ||
    !!searchLabsRequestAttrs?.filterAttributes?.externalref || !!searchLabsRequestAttrs?.filterAttributes?.id;

  pageData.isActive = true; // set isActive to true in the newest gathered page response

  pageData.timeFetched = Date.now();

  pageData.renderedOnServer = typeof window === 'undefined';

  pageData.pageAttributes = {
    ...searchLabsRequestAttrs
  };

  if (!pageData.searchListingPage) {
    pageData.ucmsCategorization = getUCMSCategorizationFromUCMSHierarchy(
      pageData.pageAttributes.filterAttributes, ucmsHierarchy
    );
  }

  const pageUrlStart = Configuration.get('listingPage.listingPageUrlStart');

  pageData.filtersFromSearchLabs = convertFiltersFromSearchLabsToReduxFormat(
    searchLabsAPIData, pageUrlStart, pageData.pageAttributes, pageData.ucmsCategorization
  );

  const selectedSingleBrandFilter = getSingleSelectedBrandFilter(pageData.filtersFromSearchLabs);
  const selectedSingleTypeFilter = getSingleSelectedTypeFilter(pageData.filtersFromSearchLabs);

  pageData.pageUrlPrefix = pageData.searchListingPage
    ? pageUrlStart // search listing page has simple prefix
    : getPageUrlPrefixForSegmentListingPage(
      pageUrlStart,
      selectedSingleBrandFilter ? selectedSingleBrandFilter.label : null,
      selectedSingleTypeFilter ? selectedSingleTypeFilter.label : null,
      pageData.ucmsCategorization
    );
  pageData.pageUrlSuffix = createListingPageUrlFromAttributes(pageData.pageAttributes);

  pageData.sponsoredProducts = null;
  pageData.sponsoredProductsLoading = false;

  pageData.productsDeliveriesSearch = {
    searchPending: false,
    locationId: null,
    errorMessage: ''
  };

  pageData.banner =
    Array.isArray(searchLabsAPIData.banners) && searchLabsAPIData.banners.length > 0 && searchLabsAPIData.banners[0].top
      ? { htmlContent: searchLabsAPIData.banners[0].top }
      : { htmlContent: null };

  if (typeof searchLabsAPIData.general === 'object' && searchLabsAPIData.general !== null) {
    pageData.showingResultsLabel = `Showing ${searchLabsAPIData.general.page_lower} - ${searchLabsAPIData.general.page_upper} ` +
      `of ${searchLabsAPIData.general.total} results`; // TODO: add to translations
    pageData.categoryTotalProductCount = Number(searchLabsAPIData.general.total);
  }

  if (searchLabsAPIData.pagination && searchLabsAPIData.pagination.pages) {
    pageData.pagination = searchLabsAPIData.pagination.pages;
  }

  pageData.sortByOptions =
    Array.isArray(searchLabsAPIData.menus)
      ? searchLabsAPIData.menus.find((menu) => menu.type === 'sort')
      : {};
  pageData.productList = createProductList(
    productsDataFromStoreAPI, searchLabsAPIData, productIds, ucmsHierarchy, pageData.searchListingPage
  );

  const searchKeyword = searchLabsRequestAttrs?.query || searchLabsRequestAttrs?.filterAttributes?.id ||
    searchLabsRequestAttrs?.filterAttributes?.externalref ||
    // if search keyword is tag we need to add '-' character before and after search keyword
    (searchLabsRequestAttrs?.filterAttributes?.tag ? `-${searchLabsRequestAttrs.filterAttributes.tag}-` : null) ||
    // if no search keyword found, it means it's segment listing page
    null;
  
  pageData.breadcrumbData = createBreadcrumbsDataForListingPage(
    pageUrlStart, searchKeyword, searchLabsAPIData.breadcrumbs, ucmsHierarchy
  );
  

  return pageData;
}

function getUCMSCategorizationFromUCMSHierarchy(filterAttributes, ucmsHierarchy) {
  const category = ucmsHierarchy.categories.find(
    (currentCategory) => currentCategory.id === Number(filterAttributes.category_id[0])
  );
  const universe = ucmsHierarchy.universes.find(
    (currentUniverse) => currentUniverse.id === category.universeId
  );
  const market = ucmsHierarchy.markets.find(
    (currentMarket) => currentMarket.id === Number(filterAttributes.market_id[0])
  );
  const segment = ucmsHierarchy.segments.find(
    (currentSegment) => currentSegment.id === Number(filterAttributes.segment_id[0])
  );

  return {
    universeId: universe.id,
    universeName: universe.name,
    categoryId: category.id,
    categoryName: category.name,
    marketId: market.id,
    marketName: market.name,
    segmentId: segment.id,
    segmentName: segment.name
  };
}

function createProductList(productsDataFromStoreAPI, searchLabsAPIData, productIds, ucmsHierarchy, isSearchListingPage) {
  return productIds
  // Filtering of products because product can be wrongly set in Brain
  // or SearchLabs API and Store API can be not correctly synchronized
    .filter((productId) => {
      if (productsDataFromStoreAPI.findIndex((product) => product.id === productId) !== -1) {
        return true;
      }

      log.warn(`[WARNING] Product with ID ${productId} is not returned from Store API. The product is wrongly configured in Brain.
      Or SearchLabs API and Store API are not synchronized.`);
      return false;
    }).map((productId) => {
      const productDataFromStoreAPI = productsDataFromStoreAPI.find((product) => product.id === productId);
      const productDataFromSearchLabs = searchLabsAPIData.resultsets.default.results.find((product) => product.id === productId);

      const universeId = Number(productDataFromStoreAPI.categorisation.universeId);
      const categoryId = Number(productDataFromStoreAPI.categorisation.categoryId);
      const marketId = Number(productDataFromStoreAPI.categorisation.marketId);
      const segmentId = Number(productDataFromStoreAPI.categorisation.segmentId);

      const productCategorisation = {
        universeId,
        categoryId,
        marketId,
        segmentId,
        universeName: ucmsHierarchy.universes.find((universe) => universe.id === universeId).name,
        categoryName: ucmsHierarchy.categories.find((category) => category.id === categoryId).name,
        marketName: ucmsHierarchy.markets.find((market) => market.id === marketId).name,
        segmentName: ucmsHierarchy.segments.find((segment) => segment.id === segmentId).name
      };

      return {
        id: productDataFromStoreAPI.id,
        sku: productDataFromStoreAPI.sku,
        brand: { title: productDataFromStoreAPI.brand.label },
        title: productDataFromStoreAPI.label,
        boxType: productDataFromStoreAPI.boxType,
        productCategorisation: productCategorisation,
        link: productDataFromSearchLabs.isOnline === 'false' &&
          productDataFromSearchLabs.isOnlineUNS === 'true' &&
          productDataFromStoreAPI?.links?.unshackled
          // link for FMO products
          ? productDataFromStoreAPI.links.unshackled
          // link for other products
          : normalizeUrlLinkForProduct(
            productDataFromStoreAPI.id,
            productDataFromStoreAPI.link,
            productDataFromStoreAPI.brand.label,
            productDataFromStoreAPI.label,
            productCategorisation
          ),
        isFMOProduct: productDataFromSearchLabs.isOnline === 'false' &&
          productDataFromSearchLabs.isOnlineUNS === 'true' &&
          productDataFromStoreAPI?.links?.unshackled,
        image: {
          src:
            Array.isArray(productDataFromStoreAPI.images) && productDataFromStoreAPI.images[0] && // eslint-disable-line no-nested-ternary
            productDataFromStoreAPI.images[0].url
              // Because there are a very little amount of small images on dev, we use big images on dev
              ? (
                Configuration.get('listingPage.useSmallSizeProductImage')
                  ? convertImageUrlFromBrainToSmallerImage(productDataFromStoreAPI.images[0].url)
                  : productDataFromStoreAPI.images[0].url
              )
              : 'https://currys-ssl.cdn.dixons.com/grafx/images/logos/logo.png', // if image no coming from brain add Currys logo
          label:
          productDataFromStoreAPI.label
        },
        forwardOrder: productDataFromStoreAPI.forwardOrder,
        preOrder: productDataFromStoreAPI.preOrder,
        delivery: productDataFromStoreAPI.delivery,
        icons:
          Array.isArray(productDataFromStoreAPI.icons)
            ? productDataFromStoreAPI.icons.map((icon) => ({ imgUrl: icon.imageUrl, linkUrl: icon.link }))
            : undefined,
        price: normalizePriceInfoForProduct(productDataFromStoreAPI),
        promoMessages: {
          isOnlineOnly: productDataFromStoreAPI.boxType === 'sbre' || productDataFromStoreAPI.boxType === 'shd', // only SBREs and SHDs are online only
          dealsAvailable:
            Array.isArray(productDataFromStoreAPI.addedValueMessages) && productDataFromStoreAPI.addedValueMessages.length > 0
              ? productDataFromStoreAPI.addedValueMessages.map((avm) => (
                {
                  title: avm.label,
                  show: avm.isSimpleOffer // TODO: investigate from/to
                })
              )
              : undefined,
          marketingMessages:
            Array.isArray(productDataFromStoreAPI.marketingMessages) && productDataFromStoreAPI.marketingMessages.length > 0
              ? productDataFromStoreAPI.marketingMessages.map((message) => (
                {
                  class: message.iconType,
                  text: message.label
                })
              )
              : undefined,
          badges:
            Array.isArray(productDataFromStoreAPI.badges) && productDataFromStoreAPI.badges.length > 0
              ? productDataFromStoreAPI.badges.map((badge) => (
                {
                  src: badge.imageUrl,
                  label: badge.name
                })
              )
              : undefined
        },
        hasOurExpertsLoveTag:
          typeof productDataFromSearchLabs.tags === 'string' && productDataFromSearchLabs.tags.includes('Our Expert Love'),
        isFlashSale: false, // TODO: Where to find these data???
        description: productDataFromStoreAPI.fullDescription.replace(/<[^>]*>/g, ''),
        descriptionShortened: `${productDataFromStoreAPI.fullDescription.replace(/<[^>]*>/g, '')
          .substring(0, 200)}...`,
        badges:
          Array.isArray(productDataFromStoreAPI.badges)
            ? productDataFromStoreAPI.badges.map((badge) => ({ src: badge.imageUrl, label: badge.name }))
            : undefined,
        descriptionBullets: productDataFromStoreAPI?.mainFeatures?.length > 0
          ? productDataFromStoreAPI.mainFeatures.slice(0, 5)
          : [],
        stickerImage: productDataFromStoreAPI.sticker
          ? {
            src: productDataFromStoreAPI.sticker.imageUrl,
            alt: productDataFromStoreAPI.sticker.label
          } : undefined,
        ...(productDataFromStoreAPI.boxType === 'outfit' &&
          { comboKitNumberOfItemsMessage: `${productDataFromStoreAPI.subProducts.length} items in the bundle` }), // TODO: add to translation
        ...(productDataFromStoreAPI.boxType === 'outfit' &&
          {
            comboKitItemsNames:
              productDataFromStoreAPI.subProducts.map((subProduct) => `${subProduct.brand.label} ${subProduct.label}`)
                .slice(1)
          }),
        ...(productDataFromStoreAPI.boxType !== 'tariff' && // reviews are not shown for tariffs
          {
            review: {
              reviewCount: productDataFromStoreAPI.customerReview.number,
              score: productDataFromStoreAPI.customerReview.averageScore
            }
          }),
        ...(productDataFromStoreAPI.boxType !== 'download' && productDataFromStoreAPI.boxType !== 'shd' &&
          productDataFromStoreAPI.boxType !== 'sbre' && // online credit is not supported for downloads, shd and sbre
          {
            isOnlineCreditAvailable: UISettings.get('component.onlinecredit.online_credit_active') !== false,
            priceForOnlineCredit: productDataFromStoreAPI.price.amount
          }),
        checkInStock: productDataFromStoreAPI.checkInStock,
        displayCompare: !isSearchListingPage, // compare is not shown for Search Listing page
        clearance: productDataFromSearchLabs.tags.split(',').findIndex((tag) => tag.trim() === 'Clearance') !== -1,
        isSearchListingPage: isSearchListingPage
      };
    });
}

// normalizes URL link for a product's product page gathered from Store API on a listing page
// to more descriptive format because of SEO
//
// e. g. for this link for a Samsung television from Store API:
// https://www.currys.co.uk/GBUK/product-10191729-pdt.html
// it generates following link:
// https://www.currys.co.uk/gbuk/tv-and-home-entertainment/televisions/televisions/samsung-ue50ru7470uxxu-50-smart-4k-ultra-hd-hdr-led-tv-with-bixby-10191729-pdt.html
export function normalizeUrlLinkForProduct(
  productId, notNormalizedLink, productBrand, productTitle, productCategorization
) {
  const { universeName, categoryName, marketName, segmentName } = productCategorization;
  // uppercase /gbuk/ and remove everything what is after '/GBUK/' in link and convert it to lower case
  const normalizedPrefixOfLink = notNormalizedLink.replace('/gbuk/', '/GBUK/')
    .substring(0, notNormalizedLink.indexOf('/GBUK/') + 6).toLowerCase();


  // join productBrand + productTitle and trim them and replace '.' and '&' with '-'
  const trimmedProductLabel = (`${productBrand}-${productTitle}`)
    .trim().replace(/\.|&/g, '-');

  return normalizedPrefixOfLink + // eslint-disable-line prefer-template
    (`${universeName.trim()}/${categoryName.trim()}/${marketName.trim()}/` +
      // segment is added to url only if differs from market
      `${marketName.trim() !== segmentName.trim() ? `${segmentName.trim()}/` : ''}${trimmedProductLabel}`)
      // replace white spaces and substrings like ' -' or ' - ' or '- ' with '-' (even with long dash)
      .replace(/(\s*(-|–)+\s*|\s+)/g, '-')
      // remove chars which can't be in url
      .replace(/[^a-zA-Z0-9-/_]/g, '')
      // covert URL to lowercase
      .toLowerCase() + `-${productId}-pdt.html`;
}

function createBreadcrumbsDataForListingPage(pageUrlStart, searchKeyword, breadcrumbDataFromSearchLabs, ucmsHierarchy) {
  const listingPageBreadcrumbData = [];

  const siteLanguage = Configuration.get('site_language');

  // This replaces '/beta/gbuk' and '/new/gbuk' part of URL with '/gbuk' in the start of URL
  // These prefixes are used on test and production environments in URLs to new listing page
  // But for some Links in the breadcrumb, we have to remove these prefixes because these links
  // lead to the old listing page or to different pages of the Currys web (market pages, universe pages, etc)
  // TODO: remove this replacing as soon as the new listing page fully replaces the old one on the production
  const pageUrlStartWIthoutBetaOrNewPrefix =
    pageUrlStart.replace(new RegExp(`(/new/${siteLanguage})|(/beta/${siteLanguage})`), `/${siteLanguage}`);

  // Home item is always first for both segment page or search page
  listingPageBreadcrumbData[0] = {
    label: 'Home', // TODO: add to translations
    url: `${pageUrlStartWIthoutBetaOrNewPrefix}/index.html`
  };

  // For Search Listing Page, the second item is Search Item in breadcrumb
  if (searchKeyword) {
    listingPageBreadcrumbData[1] = {
      label: 'Search', // TODO: add to translations
      url: `${pageUrlStart}/search-keywords/xx_xx_xx_xx_xx/${searchKeyword}/xx-criteria.html`
    };
  }


  // if breadcrumbDataFromSearchLabs contains something,
  // then the page is segment listing page
  // or it is search page filtered by one segment
  //
  // For the segment listing page, we add following items to breadcrumb:
  // > link to universe of segment > link to category of segment > segment name
  //
  // For the search listing page filtered by one segment, we add following items to breadcrumb:
  // > link to category of filtered segment > link to market of filtered segment >
  if (typeof breadcrumbDataFromSearchLabs.segmentId === 'string') {
    const { universeId, categoryId, marketId, segmentId } = breadcrumbDataFromSearchLabs;

    const universeName = ucmsHierarchy.universes.find((universe) => universe.id === Number(universeId)).name;
    const universePartOfUrl = universeName.trim()
      // it replaces standalone ampersand with hyphen e. g. Health & wellbeing -> Health-wellbeing
      // and it also replaces any white space with hyphen e. g. Home Appliances -> Home-Appliances
      .replace(/(\s+&\s+|\s+)/g, '-')
      // it remove chars which can't be in url
      .replace(/[^a-zA-Z0-9-/_]/g, '')
      .toLowerCase();

    const categoryName = ucmsHierarchy.categories.find((category) => category.id === Number(categoryId)).name;
    const categoryPartOfUrl = categoryName.trim()
      // it replaces standalone ampersand with hyphen e. g. Health & wellbeing -> Health-wellbeing
      // and it also replaces any white space with hyphen e. g. Home Appliances -> Home-Appliances
      .replace(/(\s+&\s+|\s+)/g, '-')
      // it remove chars which can't be in url
      .replace(/[^a-zA-Z0-9-/_]/g, '')
      .toLowerCase();

    const marketName = ucmsHierarchy.markets.find((market) => market.id === Number(marketId)).name;
    const marketPartOfUrl = marketName.trim()
      // it replaces standalone ampersand with hyphen e. g. Health & wellbeing -> Health-wellbeing
      // and it also replaces any white space with hyphen e. g. Home Appliances -> Home-Appliances
      .replace(/(\s+&\s+|\s+)/g, '-')
      // it remove chars which can't be in url
      .replace(/[^a-zA-Z0-9-/_]/g, '')
      .toLowerCase();

    const segmentName = ucmsHierarchy.segments.find((segment) => segment.id === Number(segmentId)).name;


    if (!searchKeyword) {
      // adding link to universe for segment listing page
      listingPageBreadcrumbData.push({
        label: universeName,
        url: `${pageUrlStart}/${universePartOfUrl}-${universeId}-u.html`
      });
    }

    // adding link to category
    listingPageBreadcrumbData.push({
      label: categoryName,
      url: `${pageUrlStartWIthoutBetaOrNewPrefix}/${universePartOfUrl}/${categoryPartOfUrl}-${categoryId}-c.html`
    });

    if (!searchKeyword &&
      categoryName.toLowerCase() !== marketName.toLowerCase() &&
      marketName.toLowerCase() !== segmentName.toLowerCase()) {
      // if market name is different from category name and it's also different from segmentName,
      // we have to add link also to market for segment listing page
      listingPageBreadcrumbData.push({
        label:
        marketName,
        url:
          `${pageUrlStartWIthoutBetaOrNewPrefix}/${universePartOfUrl}/${categoryPartOfUrl}/${marketPartOfUrl}-${marketId}-m.html`
      });
    }

    if (searchKeyword) {
      // adding link to segment of filtered segment for search listing page
      const segmentPagePrefix = getPageUrlPrefixForSegmentListingPage(
        pageUrlStartWIthoutBetaOrNewPrefix,
        null,
        null,
        {
          universeName, categoryName, marketName, segmentName
        });

      listingPageBreadcrumbData.push({
        label: segmentName,
        url: `${segmentPagePrefix}/${categoryId}_${marketId}_${segmentId}_xx_xx/xx-criteria.html`
      });
    } else {
      // adding segment name for segment listing page
      listingPageBreadcrumbData.push({
        label: segmentName
      });
    }
  }

  if (searchKeyword) {
    // the last breadcrumb item for search listing page is search query
    listingPageBreadcrumbData.push({
      // we have to replace '+' chars with white spaces
      label: searchKeyword
        // we have to replace '+' chars with white spaces
        .replace(/\+/g, ' ')
        // we have to ASCII encoding for separator with '|' char
        .replace(/%(25)?7c/g, '|')
    });
  }

  return listingPageBreadcrumbData;
}
